/**
 * Contract between the browser extension's content script and the web app for
 * signalling that the extension is installed in this browser.
 *
 * The content script sets this attribute on `<html>` when it loads on a Hey Be
 * Safe origin (extension `content/presence-beacon.ts`); the web app reads it to
 * auto-complete the "install the extension" onboarding step (web
 * `onboarding/extensionDetection.ts`). The beacon is scoped to our own origin
 * so third-party sites can't use it to fingerprint the extension.
 *
 * The attribute value is the extension's version string, but callers should
 * treat mere presence of the attribute as the signal.
 */
export const EXTENSION_PRESENCE_ATTR = 'data-hbs-extension';

/**
 * IDs of the extension builds whose web-accessible probe resource we look for:
 * the published Chrome Web Store listing, the published Edge Add-ons listing,
 * and the pinned-key test build used against the test stack and the local CDP
 * harness.
 *
 * Every published Chromium listing needs an entry here, because a store assigns
 * its own id: the same extension installed from Edge Add-ons answers on a
 * different `chrome-extension://` origin than the CWS copy. Miss one and users
 * of that store hit exactly the blind spot this probe exists to cover — they
 * install mid-session from the onboarding link, return to the tab they left
 * open, and the step never ticks itself off because the content script was
 * never injected into it. Keep in step with EXTENSION_STORE_URLS in
 * `extensionLinks.ts`.
 *
 * Firefox is deliberately absent: its `moz-extension://` origin is a
 * per-profile random UUID, so there is no fixed URL to probe — Firefox users
 * are detected by the presence attribute alone.
 */
export const EXTENSION_PROBE_IDS = [
  'jjehmmclfmdkoimajiclfieajgoclhde',
  'jbhenilijgkmpncklfondjibojcbgphn',
  'ocbgobfeimoibdffgnegbooooojlijlf',
] as const;

/**
 * The extension resource the web app fetches to detect an install. Declared in
 * the extension manifest's `web_accessible_resources` with a `matches` of
 * `https://*.heybesafe.com/*`, so only our own origin can reach it and the
 * probe adds no fingerprinting surface for third-party sites.
 */
export const EXTENSION_PROBE_PATH = 'icons/16.png';

/**
 * URL of the probe resource for one extension id.
 *
 * Why this exists alongside {@link EXTENSION_PRESENCE_ATTR}: the attribute is
 * set by a content script, and Chrome does NOT inject content scripts into
 * tabs that were already open when the extension was installed. Someone who
 * installs from a link in the web app and returns to their existing tab is
 * therefore invisible to the attribute until they reload. Fetching a
 * web-accessible resource goes through the browser's URL handling instead of
 * an injected script, so it succeeds in that already-open tab immediately —
 * verified against a real mid-session install.
 */
export function extensionProbeUrl(extensionId: string): string {
  return `chrome-extension://${extensionId}/${EXTENSION_PROBE_PATH}`;
}
